import { useTranslation } from "react-i18next";
import { useRef, type ReactNode } from "react";
import { X, RotateCcw, ChevronDown } from "lucide-react";
import {
  ComboBox,
  Input as AriaInput,
  Button as AriaButton,
  Popover as AriaPopover,
  ListBox,
  ListBoxItem,
  type Key,
} from "react-aria-components";
import { MANUAL_STATES, type ManualState } from "@arc/contracts";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PopoverTitle } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { MANUAL_STATE_META } from "./ManualStateBadge.js";
import { EMPTY_MANUAL_FILTERS, manualFiltersActive, type ManualFilters } from "./manual-filters.js";

/**
 * The "More filters" content (mockup #16, in English) — rendered inside the toolbar's sliders POPOVER (not a modal).
 * The advanced filters behind the toolbar: a **Creator** select-with-search (scoped to THIS facility's creators,
 * never the realm), a **Status** multi-select with the real lifecycle glyphs + colours, and a **Created by me** toggle.
 * **Assigned to me** (ADR-019 assignments, ids-only read) and **Only linked** (ADR-020 `parentManualId`) are live.
 * A **Reset** (↺) clears everything. All client-side over the loaded manuals.
 */
export function ManualFiltersPanel({
  filters,
  onChange,
  creators,
  onClose,
}: {
  filters: ManualFilters;
  onChange: (f: ManualFilters) => void;
  /** The facility's own creator usernames — the ONLY options offered. */
  creators: string[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toggleStatus = (s: ManualState) => {
    const has = filters.statuses.includes(s);
    onChange({ ...filters, statuses: has ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s] });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="border-border flex items-center justify-between gap-2 border-b pb-2.5">
        <PopoverTitle>{t("manualManagement.filters.more")}</PopoverTitle>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("manualManagement.filters.reset")}
            isDisabled={!manualFiltersActive(filters)}
            onPress={() => onChange(EMPTY_MANUAL_FILTERS)}
          >
            <RotateCcw className="size-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t("common.close")} onPress={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* Creator — a select-with-search seeded from the facility's creators. */}
      <Field label={t("manualManagement.filters.creator")}>
        <CreatorCombobox
          value={filters.creator}
          options={creators}
          onChange={(creator) => onChange({ ...filters, creator })}
        />
      </Field>

      {/* Status — multi-select with the real per-state glyph + colour (not the mockup's all-red). */}
      <Field label={t("manualManagement.filters.status")}>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("manualManagement.filters.status")}>
          {MANUAL_STATES.map((s) => {
            const meta = MANUAL_STATE_META[s];
            const Icon = meta.Icon;
            const selected = filters.statuses.includes(s);
            return (
              <Button
                key={s}
                variant="outline"
                size="sm"
                aria-pressed={selected}
                onPress={() => toggleStatus(s)}
                // Selected = the per-state tint PLUS a brand-primary outline (border + ring): the draft tint
                // (`bg-muted`) is too close to unselected to read on its own, so the outline is the real "selected"
                // signal and it stays consistent across every state (mirrors the app's selected=primary convention).
                className={cn(
                  "gap-1",
                  selected ? cn(meta.className, "border-primary ring-1 ring-primary") : "text-muted-foreground",
                )}
              >
                <Icon aria-hidden="true" className="size-3.5" />
                {t(meta.labelKey)}
              </Button>
            );
          })}
        </div>
      </Field>

      {/* Created by me — functional. */}
      <SwitchRow label={t("manualManagement.filters.createdByMe")}>
        <Switch
          aria-label={t("manualManagement.filters.createdByMe")}
          isSelected={filters.createdByMe}
          onChange={(createdByMe) => onChange({ ...filters, createdByMe })}
        />
      </SwitchRow>

      {/* Assigned to me — the caller's reviewer assignments (ADR-019), an ids-only read. */}
      <SwitchRow label={t("manualManagement.filters.assignedToMe")}>
        <Switch aria-label={t("manualManagement.filters.assignedToMe")} isSelected={filters.assignedToMe} onChange={(assignedToMe) => onChange({ ...filters, assignedToMe })} />
      </SwitchRow>
      {/* Only linked — the legacy "Verknüpfung" filter; a linked copy carries `parentManualId` (ADR-020). */}
      <SwitchRow label={t("manualManagement.filters.onlyLinked")}>
        <Switch aria-label={t("manualManagement.filters.onlyLinked")} isSelected={filters.onlyLinked} onChange={(onlyLinked) => onChange({ ...filters, onlyLinked })} />
      </SwitchRow>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </div>
  );
}

function SwitchRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">
        {label}
        {hint ? <span className="text-muted-foreground ml-2 text-xs">{hint}</span> : null}
      </span>
      {children}
    </div>
  );
}

/** A select-with-search over the facility's creators (react-aria ComboBox). Clearing the input → no creator filter. */
function CreatorCombobox({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <ComboBox
      aria-label={t("manualManagement.filters.creator")}
      selectedKey={value}
      onSelectionChange={(key: Key | null) => onChange(key == null ? null : String(key))}
      menuTrigger="focus"
      allowsEmptyCollection
    >
      <div className="relative">
        <AriaInput
          ref={inputRef}
          placeholder={t("manualManagement.filters.searchCreator")}
          // Focus uses the tenant brand primary (takeda red), not the generic muted `--ring`. Extra right padding
          // when a creator is picked so the text never runs under the clear + chevron affordances.
          className={cn(
            "border-border bg-background h-8 w-full rounded-md border px-2 text-sm outline-none",
            "focus-visible:border-primary focus-visible:ring-primary/40 focus-visible:ring-2",
            value != null ? "pr-14" : "pr-8",
          )}
        />
        {/* Clear the selected creator. `slot={null}` opts this button OUT of the ComboBox's trigger slot, so its
            onPress clears the selection instead of toggling the listbox (react-aria pattern). This button unmounts
            the instant value→null, so we hand focus back to the input first — otherwise a keyboard/SR user who
            activated it would be dropped to <body> (panel finding). The input is the logical landing spot. */}
        {value != null ? (
          <AriaButton
            slot={null}
            aria-label={t("manualManagement.filters.clearCreator")}
            onPress={() => {
              onChange(null);
              inputRef.current?.focus();
            }}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-primary/40 absolute top-1/2 right-7 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 outline-none focus-visible:ring-2"
          >
            <X aria-hidden="true" className="size-4" />
          </AriaButton>
        ) : null}
        <AriaButton
          aria-label={t("manualManagement.filters.showCreators")}
          className="text-muted-foreground absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 outline-none"
        >
          <ChevronDown aria-hidden="true" className="size-4" />
        </AriaButton>
      </div>
      <AriaPopover className="border-border bg-popover text-popover-foreground z-50 w-[var(--trigger-width)] rounded-md border shadow-md outline-none">
        <ListBox
          className="max-h-60 overflow-auto p-1 outline-none"
          renderEmptyState={() => <div className="text-muted-foreground p-2 text-sm">No creators to filter by.</div>}
        >
          {options.map((o) => (
            <ListBoxItem
              key={o}
              id={o}
              textValue={o}
              className="data-[focused]:bg-accent data-[focused]:text-accent-foreground data-[selected]:font-medium flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-none"
            >
              {o}
            </ListBoxItem>
          ))}
        </ListBox>
      </AriaPopover>
    </ComboBox>
  );
}
